import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { MessageCircle, Eye, EyeOff } from "lucide-react";
import { useCompleteInvite } from "@/hooks/useInvites";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PolkaDotCard } from "@/components/illustrations/PolkaDotCard";
import { WavyLineDecoration } from "@/components/illustrations/WavyLineDecoration";
import { ApplicationBrandLogo } from "@/components/icons/ApplicationBrandLogo";
import { useTranslation } from "react-i18next";

export default function Login() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, signInWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const completeInvite = useCompleteInvite();
  const { toast } = useToast();
  const { t: tAuth } = useTranslation("auth");
  const { t: tCommon } = useTranslation("common");

  useEffect(() => {
    if (user) {
      const inviteToken = searchParams.get("inviteToken");
      if (inviteToken) {
        // Se tem invite token e já está logado, processar o convite
        handleCompleteInvite(inviteToken, user.id);
      } else {
        navigate("/calendar", { replace: true });
      }
    }
  }, [user, navigate, searchParams]);

  const handleCompleteInvite = async (inviteToken: string, userId: string) => {
    try {
      await completeInvite.mutateAsync({ token: inviteToken, userId });
      toast({
        title: tAuth("inviteAccepted"),
        description: tAuth("inviteAcceptedDescription"),
      });
      navigate("/calendar");
    } catch (error: unknown) {
      toast({
        title: tAuth("inviteAcceptError"),
        description: error instanceof Error ? error.message : tAuth("inviteAcceptError"),
        variant: "destructive",
      });
      navigate("/calendar");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const { error } = await signIn({ email, password, rememberMe: false });

    if (!error) {
      const inviteToken = searchParams.get("inviteToken");
      if (inviteToken) {
        setTimeout(async () => {
          const { data: { user: currentUser } } = await supabase.auth.getUser();
          if (currentUser) {
            await handleCompleteInvite(inviteToken, currentUser.id);
          } else {
            navigate("/calendar");
          }
        }, 500);
      } else {
        navigate("/calendar");
      }
    }

    setIsLoading(false);
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    await signInWithGoogle();
    setIsLoading(false);
  };

  const whatsappLink = `https://wa.me/5511962672372?text=${encodeURIComponent('Oi! Tudo bem? Gostaria de saber mais sobre o Akuna Pet!')}`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-login p-4 md:p-8 relative overflow-hidden">
      {/* Decorative elements */}



      {/* Main content grid */}
      <div className="w-full max-w-6xl grid grid-cols-1 items-center relative z-10">

        {/* Right column - Login form */}
        <div className="w-full max-w-md mx-auto relative">
          <div className="absolute bottom-[100px] left-[-180px] hidden lg:block opacity-40">
            {/* <img src="/assets/images/rabo.png" alt="rabo background" style={{
              width: "220px",
              height: "210px",
              filter: "contrast(0.7) saturate(3) hue-rotate(329deg)",
              objectFit: "scale-down",
            }} /> */}
          </div>
          <div className="mb-8 flex flex-col items-center justify-center gap-4">
            <ApplicationBrandLogo variant="auth" />
            <h2 className="text-2xl font-bold text-foreground mb-2">{tAuth("loginTitle")}</h2>
          </div>

          <div className="bg-card rounded-2xl shadow-xl p-8 border border-border">
            {/* <Button
              type="button"
              variant="outline"
              className="w-full mb-6 h-12 text-base hover:bg-primary/10 hover:text-primary transition-colors"
              onClick={handleGoogleLogin}
              disabled={isLoading}
            >
              <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Entrar com Google
            </Button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">ou</span>
              </div>
            </div> */}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{tCommon("email")}</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="email-input"
                  placeholder={tAuth("emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{tCommon("password")}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    data-testid="password-input"
                    placeholder={tAuth("passwordPlaceholder")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-3 flex items-center text-muted-foreground hover:text-foreground focus:outline-none"
                    aria-label={showPassword ? tAuth("hidePassword") : tAuth("showPassword")}
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full h-11" disabled={isLoading} data-testid="login-submit">
                {isLoading ? tAuth("signingIn") : tAuth("signIn")}
              </Button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-muted-foreground mb-1 text-xs">
                {tAuth("noAccountYet")}
              </p>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm inline-flex items-center gap-1 text-primary underline font-medium"
              >
                <MessageCircle className="h-4 w-4" />
                {tAuth("ourTeamCreates")}
              </a>
            </div>
          </div>

          {/* Footer text */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            {tAuth("agreeTerms")}{" "}
            <Link to="/terms-of-service" className="text-primary underline font-medium">
              {tAuth("termsOfService")}
            </Link>
          </p>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 w-full">
        <p className="text-center text-xs font-medium text-primary">
          © 2026 Akuna Sistemas Digitais LTDA. {tCommon("allRightsReserved")}.
        </p>
      </div>

    </div>
  );
}
